import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Box,
  Paper,
  Chip,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import {
  CheckCircle2,
  PauseCircle,
  Clock,
  AlertCircle,
  X as CloseIcon,
  ChevronRight,
} from "lucide-react";
import api from "../../services/api";
import { useToast } from "../../hooks/useToast";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    maxWidth: 520,
    width: "100%",
  },
  dialogTitle: {
    padding: theme.spacing(2, 2.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogContent: {
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  statusCard: {
    padding: theme.spacing(2),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(59, 130, 246, 0.08)"
          : "#f0f7ff",
    },
  },
  statusCardActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(59, 130, 246, 0.12)"
        : "#e0f2fe",
  },
  onlineCard: {
    borderLeft: "5px solid #10b981",
  },
  pauseCard: {
    borderLeft: "5px solid #f59e0b",
  },
  messagePreview: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    backgroundColor:
      theme.palette.type === "dark" ? "#1e222d" : "#f8fafc",
    border: `1px solid ${theme.palette.divider}`,
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    marginTop: theme.spacing(0.5),
  },
  confirmBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(245, 158, 11, 0.15)"
        : "#fffbeb",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(245, 158, 11, 0.3)"
        : "#fcd34d"
    }`,
    marginTop: theme.spacing(1),
  },
  actionBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
  },
}));

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return "Até retomar manualmente";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (remaining === 0) return hours === 1 ? "1 hora" : `${hours} horas`;
  return `${hours}h ${remaining}m`;
};

const UserStatusModal = ({ open, onClose }) => {
  const classes = useStyles();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusData, setStatusData] = useState({
    online: true,
    pausePolicyId: null,
    pauseStartedAt: null,
    currentPolicy: null,
    availablePolicies: [],
  });
  const [selectedPolicyToConfirm, setSelectedPolicyToConfirm] = useState(null);

  const loadStatus = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await api.get("/user-pause-status");
      setStatusData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    loadStatus();
    setSelectedPolicyToConfirm(null);
  }, [loadStatus]);

  const isCurrentOnline =
    !statusData.pausePolicyId && (statusData.online ?? true);

  const handleSetOnline = async () => {
    setSubmitting(true);
    try {
      await api.post("/user-pause-status", {
        status: "online",
      });
      toast.success("Você agora está Online! Nenhuma mensagem foi enviada.");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectPolicy = (policy) => {
    if (statusData.pausePolicyId === policy.id) {
      // Já está nessa pausa
      return;
    }
    setSelectedPolicyToConfirm(policy);
  };

  const handleConfirmPause = async () => {
    if (!selectedPolicyToConfirm) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/user-pause-status", {
        status: "pause",
        pausePolicyId: selectedPolicyToConfirm.id,
      });

      toast.success(
        data.message ||
          "Pausa ativada! O aviso será enviado automaticamente se o cliente mandar uma mensagem."
      );

      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box display="flex" alignItems="center" gap={1}>
          <PauseCircle size={22} color="#3b82f6" />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Alterar Status do Atendente
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={onClose}
          style={{ minWidth: 32, padding: 4 }}
        >
          <CloseIcon size={18} />
        </Button>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress size={32} />
          </Box>
        ) : selectedPolicyToConfirm ? (
          /* Confirmação de ativação de pausa */
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              Confirmar Entrada em Pausa: {selectedPolicyToConfirm.name}
            </Typography>

            <Paper elevation={0} className={classes.confirmBox}>
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <AlertCircle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    Aviso Automático sob Demanda
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    A mensagem não é enviada agora. Ela será enviada ao cliente uma única vez somente se ele mandar uma mensagem durante a sua pausa:
                  </Typography>
                  <div className={classes.messagePreview}>
                    "{selectedPolicyToConfirm.message}"
                  </div>
                </div>
              </Box>
            </Paper>

            <Box display="flex" justifyContent="flex-end" gap={1.5} mt={1}>
              <Button
                variant="outlined"
                onClick={() => setSelectedPolicyToConfirm(null)}
                disabled={submitting}
                className={classes.actionBtn}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmPause}
                disabled={submitting}
                className={classes.actionBtn}
              >
                {submitting ? "Ativando..." : "Confirmar e Ativar Pausa"}
              </Button>
            </Box>
          </Box>
        ) : (
          /* Lista de Status */
          <>
            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 700 }}>
              SELECIONE SEU STATUS:
            </Typography>

            {/* Opção ONLINE */}
            <Paper
              elevation={0}
              className={`${classes.statusCard} ${classes.onlineCard} ${
                isCurrentOnline ? classes.statusCardActive : ""
              }`}
              onClick={handleSetOnline}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <CheckCircle2 size={22} color="#10b981" />
                  <div>
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                      Online (Disponível)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Disponível para atendimentos normais. Não envia nenhuma mensagem ao cliente.
                    </Typography>
                  </div>
                </Box>
                {isCurrentOnline ? (
                  <Chip size="small" label="Ativo" style={{ backgroundColor: "#d1fae5", color: "#065f46", fontWeight: 700 }} />
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    style={{ backgroundColor: "#10b981", color: "#fff", textTransform: "none", fontWeight: 600 }}
                  >
                    Ficar Online
                  </Button>
                )}
              </Box>
            </Paper>

            <Divider style={{ margin: "4px 0" }} />

            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 700 }}>
              POLÍTICAS DE PAUSA DISPONÍVEIS:
            </Typography>

            {statusData.availablePolicies.length === 0 ? (
              <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                Nenhuma política de pausa configurada pela empresa.
              </Typography>
            ) : (
              statusData.availablePolicies.map((policy) => {
                const isCurrentPause =
                  statusData.pausePolicyId === policy.id;
                return (
                  <Paper
                    key={policy.id}
                    elevation={0}
                    className={`${classes.statusCard} ${classes.pauseCard} ${
                      isCurrentPause ? classes.statusCardActive : ""
                    }`}
                    onClick={() => handleSelectPolicy(policy)}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <PauseCircle size={22} color="#f59e0b" />
                        <div>
                          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            {policy.name}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Clock size={13} color="#64748b" />
                            <Typography variant="caption" color="textSecondary">
                              Previsão: {formatDuration(policy.durationMinutes)}
                            </Typography>
                          </Box>
                        </div>
                      </Box>
                      {isCurrentPause ? (
                        <Chip
                          size="small"
                          label="Pausa Atual"
                          style={{ backgroundColor: "#fef3c7", color: "#92400e", fontWeight: 700 }}
                        />
                      ) : (
                        <ChevronRight size={18} color="#94a3b8" />
                      )}
                    </Box>
                    <div className={classes.messagePreview}>
                      Mensagem: "{policy.message}"
                    </div>
                  </Paper>
                );
              })
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserStatusModal;

